/**
 * BaseApiClient
 * 
 * Base class for all API clients with common HTTP request logic,
 * error handling, authentication, and configuration
 * 
 * IMPORTANT: Classes extending BaseApiClient should use relative paths WITHOUT /api/v1 prefix
 * Example: this.post('/login', data) NOT this.post('/api/v1/login', data)
 * The /api/v1 prefix is automatically added by this base class.
 * 
 * Note: Standalone services that use fetch() directly should include the full /api/v1 path.
 */

import {
  resolveSharedAuthToken,
  normalizeBearerAuthToken,
  setCrossSubdomainAuthCookie,
  clearCrossSubdomainAuthCookie,
} from '@vaiu/utils';

/**
 * v1 paths that must not send Bearer. Edge authorizers often run before the handler;
 * a stale or guest JWT on /login can yield 403 "not authorized" instead of 401.
 */
function isAnonymousAuthEndpoint(endpoint: string): boolean {
  const path = endpoint.split('?')[0];
  if (path === '/login' || path === '/register' || path === '/reset-password') return true;
  if (path.startsWith('/auth/otp/') || path === '/auth/google') return true;
  return false;
}

export class BaseApiClient {
  protected baseURL: string;
  protected token: string | null = null;
  protected useMockData = false;

  constructor() {
    // Always use relative URLs - in development this uses Vite's proxy,
    // in production this assumes the API is served from the same domain
    this.baseURL = '/api/v1';
    
    if (import.meta.env.DEV) {
      console.log('🔧 Development mode: Using relative baseURL for proxy:', this.baseURL);
    } else {
      console.log('🔧 Production mode: Using relative baseURL (same-origin):', this.baseURL);
    }
    
    // Auto-load token: localStorage, or shared .vaiu.app cookie (sell.vaiu.app ↔ vaiu.app)
    const existingToken = resolveSharedAuthToken();
    if (existingToken) {
      this.token = existingToken;
      console.log('🔑 Auto-loaded existing auth token (localStorage or cross-subdomain cookie)');
    }
    
    // Enable mock data in development when backend is not available
    this.useMockData = import.meta.env.DEV && !import.meta.env.PLATFORM_API_URL;
  }

  /**
   * Set authentication token
   */
  setToken(token: string) {
    const normalized = normalizeBearerAuthToken(token);
    if (!normalized) {
      this.removeToken();
      return;
    }
    this.token = normalized;
    localStorage.setItem('authToken', normalized);
    setCrossSubdomainAuthCookie(normalized);
  }

  /**
   * Remove authentication token
   */
  removeToken() {
    this.token = null;
    localStorage.removeItem('authToken');
    clearCrossSubdomainAuthCookie();
  }

  /**
   * Resolve token from memory, localStorage, or shared vaiu.app subdomain cookie
   */
  private resolveAuthToken(): string | null {
    if (this.token) return this.token;
    const resolved = resolveSharedAuthToken();
    if (resolved) this.token = resolved;
    return resolved;
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return this.resolveAuthToken();
  }

  /**
   * Check if client is authenticated
   */
  isAuthenticated(): boolean {
    return this.token !== null;
  }

  /**
   * Make HTTP request with standardized error handling
   */
  async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    // Smart version detection: if endpoint starts with v2, v3, etc., use /api/ prefix only
    // Otherwise, use the full baseURL (/api/v1)
    const versionPattern = /^v\d+\//;
    const isVersioned = versionPattern.test(endpoint);
    const basePrefix = isVersioned ? '/api/' : this.baseURL;
    const url = `${basePrefix}${endpoint}`;
    
    // Log URL construction in development
    if (import.meta.env.DEV) {
      if (isVersioned) {
        console.log(`🔄 [BaseApiClient] Detected versioned endpoint: ${endpoint} -> ${url}`);
      } else {
        console.log(`🔵 [BaseApiClient] Non-versioned endpoint: ${endpoint} -> ${url}`);
      }
    }
    
    // Initialize headers from options (if provided)
    // Note: For FormData, Content-Type should NOT be set - browser sets it with boundary
    const headers: Record<string, string> = {};
    
    // Merge with any provided headers first
    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }
    
    // Only set Content-Type to application/json if:
    // 1. Not already set (to avoid overriding FormData's Content-Type)
    // 2. Body is not FormData
    // 3. Body exists (not a GET request)
    if (!headers['Content-Type'] && !(options.body instanceof FormData) && options.body) {
      headers['Content-Type'] = 'application/json';
    }

    // SECURITY: Client-key should NEVER be sent from browser!
    // The Vite dev proxy or production API gateway should add this header server-side
    // Removing client-key from frontend for security

    // Instance, localStorage, or shared .vaiu.app cookie (cross-subdomain SSO)
    const authToken = this.resolveAuthToken();
    const attachBearer = Boolean(authToken) && !isAnonymousAuthEndpoint(endpoint);

    if (attachBearer) {
      headers.Authorization = `Bearer ${authToken}`;
      console.log('🔑 Auth token added to request (length:', authToken.length, ')');
      console.log('🔑 Token source:', this.token ? 'instance' : 'localStorage');
      console.log('🔑 Token preview:', authToken.substring(0, 20) + '...');
      
      // Update instance token if we found one in localStorage but didn't have it
      if (!this.token && authToken) {
        this.token = authToken;
        console.log('🔑 Updated instance token from localStorage');
      }
      
      // Try to decode JWT payload to see what's inside (for debugging)
      try {
        const parts = authToken.split('.');
        // console.log('🔍 JWT parts count:', parts.length);
        if (parts.length === 3) {
          // console.log('🔍 JWT header:', parts[0]);
          // console.log('🔍 JWT payload (base64):', parts[1]);
          // console.log('🔍 JWT signature:', parts[2].substring(0, 20) + '...');
          
          const payload = JSON.parse(atob(parts[1]));
          // console.log('🔍 JWT payload decoded:', payload);
          // console.log('🔍 JWT payload debug:', {
          //   userId: payload._id || payload.sub || payload.id,
          //   exp: payload.exp ? new Date(payload.exp * 1000) : 'No expiry',
          //   iat: payload.iat ? new Date(payload.iat * 1000) : 'No issued at',
          //   currentTime: new Date(),
          //   isExpired: payload.exp ? (Date.now() / 1000) > payload.exp : false
          // });
        } else {
          // console.warn('🔍 Invalid JWT format - expected 3 parts, got:', parts.length);
        }
      } catch (jwtError) {
        // console.warn('🔍 Could not decode JWT payload:', jwtError);
        // console.warn('🔍 Token that failed:', authToken.substring(0, 50) + '...');
      }
    } else {
      delete headers.Authorization;
      if (authToken && import.meta.env.DEV) {
        console.log('🔓 Omitting Bearer for anonymous auth endpoint:', endpoint);
      }
      if (!authToken) {
        console.log('⚠️ No auth token available for request to:', endpoint);
        console.log('⚠️ this.token:', this.token);
        console.log('⚠️ localStorage authToken:', localStorage.getItem('authToken'));
      }
    }

    // NEVER add client-key header from client-side code!
    // In development: Vite proxy adds the client-key header
    // In production: API gateway/server should handle API key authentication
    // Client-side code should NEVER have access to API keys for security reasons

    try {
      console.log(`🌐 Making API request to: ${url}`);
      console.log(`📤 Method: ${options.method || 'GET'}`);
      console.log(`📋 Headers:`, headers);
      console.log(`📦 Body type:`, options.body?.constructor?.name);
      console.log(`📦 Is FormData:`, options.body instanceof FormData);
      if (options.body instanceof FormData) {
        console.log(`📦 FormData entries:`, Array.from(options.body.entries()).map(([key, value]) => 
          [key, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value]
        ));
      }
      
      // For FormData, don't pass headers at all - let browser set Content-Type with boundary
      // For other requests, pass headers normally
      const isFormData = options.body instanceof FormData;
      const fetchOptions: RequestInit = {
        ...options,
        method: options.method || 'GET',
      };
      
      // Only set headers if not FormData (browser handles FormData headers automatically)
      if (!isFormData) {
        fetchOptions.headers = headers;
      } else {
        // For FormData, only include Authorization header if present, let browser set Content-Type
        if (headers.Authorization) {
          fetchOptions.headers = {
            Authorization: headers.Authorization
          };
        }
      }
      
      const response = await fetch(url, fetchOptions);

      console.log(`📥 Response status: ${response.status} ${response.statusText}`);
      console.log(`📥 Response URL: ${response.url}`);

      if (!response.ok) {
        if (response.status === 401) {
          // Don't automatically remove token here - let AuthContext handle it
          // This prevents silent logout when navigating between pages
          // Note: Guest users should have full API access - if getting 401s for guests,
          // the backend needs to be updated to support guest user operations
          console.warn('🚨 API returned 401, but not auto-removing token to prevent silent logout');
          const e401 = new Error('Authentication required - please log in again') as Error & {
            status: number;
          };
          e401.status = 401;
          throw e401;
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorBody: unknown = undefined;

        // Try to get response body as text first (can be consumed once)
        try {
          const responseText = await response.text();
          console.log('🔍 Response body as text:', responseText);

          if (responseText) {
            // Try to parse as JSON
            try {
              const errorData = JSON.parse(responseText);
              errorBody = errorData;
              console.log('🔍 Parsed error data:', errorData);

              // Extract error message from various possible formats (prefer JSON `error.message`)
              if (typeof errorData === 'string') {
                errorMessage = errorData;
              } else if (errorData?.error?.message) {
                // Handle nested error objects like {error: {message: "...", name: "..."}}
                errorMessage = String(errorData.error.message);
              } else if (errorData?.error && typeof errorData.error === 'string') {
                errorMessage = errorData.error;
              } else if (typeof errorData?.message === 'string') {
                errorMessage = errorData.message;
              } else if (typeof errorData?.detail === 'string') {
                errorMessage = errorData.detail;
              } else if (errorData?.statusText) {
                errorMessage = String(errorData.statusText);
              } else if (typeof errorData === 'object') {
                errorMessage = `${response.status}: ${JSON.stringify(errorData, null, 2)}`;
              }
            } catch (jsonError) {
              console.log('🔍 Response is not JSON, using as text');
              // If it's not JSON, use the text as error message
              errorMessage =
                responseText.length > 200
                  ? `${response.status}: ${responseText.substring(0, 200)}...`
                  : `${response.status}: ${responseText}`;
            }
          }
        } catch (textError) {
          console.log('🔍 Error reading response body:', textError);
          errorMessage = `${response.status}: ${response.statusText}`;
        }

        console.log('🔍 Final error message:', errorMessage);
        const httpErr = new Error(errorMessage) as Error & { status: number; body?: unknown };
        httpErr.status = response.status;
        if (errorBody !== undefined) {
          httpErr.body = errorBody;
        }
        throw httpErr;
      }

      // Check content type before parsing
      const contentType = response.headers.get('content-type');
      console.log(`📥 Response content-type: ${contentType}`);
      
      if (contentType && contentType.includes('text/html')) {
        // Meteor web shell, SPA index, WAF page, etc. — not JSON API
        const htmlText = await response.text();
        const preview = htmlText.substring(0, 600);
        console.error(
          `❌ Received HTML instead of JSON for ${url} (check Vite proxy / PLATFORM_API_URL):`,
          preview + (htmlText.length > 600 ? '...' : '')
        );
        const isMeteorShell =
          htmlText.includes('__meteor_runtime_config__') || htmlText.includes('meteorRelease');
        if (isMeteorShell) {
          throw new Error(
            `API returned Meteor HTML for ${url}. Other /api/v1 routes may still work if only this path is missing or falls through to the web shell. Fix: ensure this URL serves JSON (curl it against your API origin), or point PLATFORM_API_URL at the host where REST is mounted. See apps/vaiu-app/docs/TROUBLESHOOTING.md.`
          );
        }
        throw new Error(
          `API returned HTML instead of JSON for ${url}. Adjust PLATFORM_API_URL / proxy so this path hits the JSON API. See apps/vaiu-app/vite.config.ts and docs/TROUBLESHOOTING.md.`
        );
      }
      
      // Try to parse JSON response
      try {
        const responseData = await response.json();
        console.log(`📥 Response data:`, responseData);
        return responseData;
      } catch (jsonError) {
        console.error('❌ Failed to parse JSON response:', jsonError);
        throw new Error('Invalid response format from server. Please try again later.');
      }
    } catch (error) {
      console.error(`❌ API request failed:`, error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  /**
   * Make GET request
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make POST request
   */
  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    // Check if data is FormData - if so, pass it directly without JSON.stringify
    const isFormData = data instanceof FormData;
    
    // Prepare headers - don't set Content-Type for FormData
    const headers: Record<string, string> = {};
    if (!isFormData && data) {
      headers['Content-Type'] = 'application/json';
    }
    // Merge with any provided headers
    if (options?.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }
    
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      headers: Object.keys(headers).length > 0 ? headers : undefined
    });
  }

  /**
   * Make PUT request
   */
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make PATCH request
   */
  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
} 